/**
 * Bible references in AI Rob's answers ("Matthew 5:44", "1 Corinthians
 * 13:4–7", "John 3:16; 5:24"), found in the text so a tap can open the
 * passage: renderMarkdown's `scripture` option marks them, ChatView opens
 * them. At rest a reference looks exactly like the words around it.
 *
 * Pure, with no imports, so scripts/check-scripture.js runs exactly this file
 * for $0. No pattern here may look behind itself: Safari before 16.4 refuses
 * to run a script that contains one, this file is in the main bundle, and an
 * older iPhone would get a blank app. The character before a match is
 * checked in code instead.
 *
 * What counts as a reference:
 *   - a book (by name; an abbreviation only with a chapter:verse), then a
 *     chapter, then optionally verses: a range (which may cross chapters), a
 *     comma list, "chapter N, verse M", or a chapter range ("Matthew 5–7")
 *   - "; 5:24", ", 5:24", " and 5:24" or ", or 5:24" right after one is its own
 *     reference, in the same book (a bare "and 12" is not: "Hebrews 11 and 12
 *     others", "Matthew 5, 6, and 7" link only the first)
 *   - "the 23rd Psalm" is Psalm 23
 *   - words are joined by a space or a no-break space, never a line break
 *   - the chapter is one the book has, every verse is 1–176, and the book
 *     number exists (no 3 Peter); in a one-chapter book a bare number is a verse
 * and what never does:
 *   - a match right after a letter or a digit, or after a number no book has
 *     ("4 John 1:1"), or with the book name in lowercase
 *   - a match followed by a digit, ":digit", ".digit", ",digit", a letter,
 *     am/pm or %, so "Mark 10:30 p.m." and "Genesis 1,000" stay plain
 *   - a name that is also a common word or a first name (Mark, John, Acts…)
 *     with a bare chapter ("Acts 2"), unless the word "chapter", a verse, a
 *     chapter range or a confirmed reference beside it in the same list
 *     ("John 1, Colossians 1, and Hebrews 1") says it is the book; and never
 *     when a counted word follows it ("Mark 2 people", "Mark 4 times"), even
 *     beside a reference
 */

// [name, USFM code, chapters, other names, abbreviations, flags]. A numbered
// book carries its number in the name ("1 Samuel"); its other names and
// abbreviations are written without it, since "1", "I", "First" or "1st"
// comes before any of them. flags: 'amb', a common word or first name (see
// above); 'one:N', a single chapter of N verses.
export const BOOKS = [
  ['Genesis', 'GEN', 50, [], ['Gen']],
  ['Exodus', 'EXO', 40, [], ['Exod', 'Ex']],
  ['Leviticus', 'LEV', 27, [], ['Lev']],
  ['Numbers', 'NUM', 36, [], ['Num'], 'amb'],
  ['Deuteronomy', 'DEU', 34, [], ['Deut']],
  ['Joshua', 'JOS', 24, [], ['Josh']],
  ['Judges', 'JDG', 21, [], ['Judg'], 'amb'],
  ['Ruth', 'RUT', 4, [], []],
  ['1 Samuel', '1SA', 31, [], ['Sam']],
  ['2 Samuel', '2SA', 24, [], ['Sam']],
  ['1 Kings', '1KI', 22, [], ['Kgs']],
  ['2 Kings', '2KI', 25, [], ['Kgs']],
  ['1 Chronicles', '1CH', 29, [], ['Chron', 'Chr']],
  ['2 Chronicles', '2CH', 36, [], ['Chron', 'Chr']],
  ['Ezra', 'EZR', 10, [], []],
  ['Nehemiah', 'NEH', 13, [], ['Neh']],
  ['Esther', 'EST', 10, [], ['Esth']],
  ['Job', 'JOB', 42, [], [], 'amb'],
  ['Psalms', 'PSA', 150, ['Psalm'], ['Ps', 'Psa', 'Pss']],
  ['Proverbs', 'PRO', 31, [], ['Prov']],
  ['Ecclesiastes', 'ECC', 12, [], ['Eccl', 'Eccles', 'Ecc']],
  ['Song of Songs', 'SNG', 8, ['Song of Solomon'], []],
  ['Isaiah', 'ISA', 66, [], ['Isa']],
  ['Jeremiah', 'JER', 52, [], ['Jer']],
  ['Lamentations', 'LAM', 5, [], ['Lam']],
  ['Ezekiel', 'EZK', 48, [], ['Ezek']],
  ['Daniel', 'DAN', 12, [], ['Dan']],
  ['Hosea', 'HOS', 14, [], ['Hos']],
  ['Joel', 'JOL', 3, [], []],
  ['Amos', 'AMO', 9, [], []],
  ['Obadiah', 'OBA', 1, [], ['Obad'], 'one:21'],
  ['Jonah', 'JON', 4, [], []],
  ['Micah', 'MIC', 7, [], ['Mic']],
  ['Nahum', 'NAM', 3, [], ['Nah']],
  ['Habakkuk', 'HAB', 3, [], ['Hab']],
  ['Zephaniah', 'ZEP', 3, [], ['Zeph']],
  ['Haggai', 'HAG', 2, [], ['Hag']],
  ['Zechariah', 'ZEC', 14, [], ['Zech']],
  ['Malachi', 'MAL', 4, [], ['Mal']],
  ['Matthew', 'MAT', 28, [], ['Matt', 'Mt']],
  ['Mark', 'MRK', 16, [], ['Mk'], 'amb'],
  ['Luke', 'LUK', 24, [], ['Lk']],
  ['John', 'JHN', 21, [], ['Jn'], 'amb'],
  ['Acts', 'ACT', 28, [], [], 'amb'],
  ['Romans', 'ROM', 16, [], ['Rom']],
  ['1 Corinthians', '1CO', 16, [], ['Cor']],
  ['2 Corinthians', '2CO', 13, [], ['Cor']],
  ['Galatians', 'GAL', 6, [], ['Gal']],
  ['Ephesians', 'EPH', 6, [], ['Eph']],
  ['Philippians', 'PHP', 4, [], ['Phil', 'Php']],
  ['Colossians', 'COL', 4, [], ['Col']],
  ['1 Thessalonians', '1TH', 5, [], ['Thess']],
  ['2 Thessalonians', '2TH', 3, [], ['Thess']],
  ['1 Timothy', '1TI', 6, [], ['Tim']],
  ['2 Timothy', '2TI', 4, [], ['Tim']],
  ['Titus', 'TIT', 3, [], []],
  ['Philemon', 'PHM', 1, [], ['Philem', 'Phlm'], 'one:25'],
  ['Hebrews', 'HEB', 13, [], ['Heb']],
  ['James', 'JAS', 5, [], ['Jas'], 'amb'],
  ['1 Peter', '1PE', 5, [], ['Pet']],
  ['2 Peter', '2PE', 3, [], ['Pet']],
  ['1 John', '1JN', 5, [], ['Jn']],
  ['2 John', '2JN', 1, [], ['Jn'], 'one:13'],
  ['3 John', '3JN', 1, [], ['Jn'], 'one:15'],
  ['Jude', 'JUD', 1, [], [], 'amb one:25'],
  ['Revelation', 'REV', 22, ['Revelations'], ['Rev']],
];

const MAX_VERSE = 176; // Psalm 119, the longest chapter

// This file is in the main bundle, which Babel takes down to ES5: it keeps
// to forEach, Object.assign and Array.from, which need none of Babel's
// inlined helpers (spreads, for…of and array destructuring would add ~4 KB).
const INFO = BOOKS.map((b) => {
  const numbered = /^([123]) (.+)$/.exec(b[0]);
  const one = /\bone:(\d+)/.exec(b[5] || '');
  return {
    name: b[0],
    code: b[1],
    chapters: b[2],
    n: numbered ? Number(numbered[1]) : 0,
    base: numbered ? numbered[2] : b[0],
    others: b[3] || [],
    abbrevs: b[4] || [],
    verses: one ? Number(one[1]) : null,
    amb: /\bamb\b/.test(b[5] || ''),
  };
});

// Every spelling to its book: a plain one to its row; a numbered one
// ("Corinthians", "Cor") to a row for each number the book has.
const PLAIN = new Map();
const NUMBERED = new Map();
INFO.forEach((book, row) => {
  const add = (spelling, abbrev) => {
    if (!book.n) {
      PLAIN.set(spelling, { row, abbrev });
      return;
    }
    const entry = NUMBERED.get(spelling) || { rows: {}, abbrev };
    entry.rows[book.n] = row;
    NUMBERED.set(spelling, entry);
  };
  add(book.base, false);
  book.others.forEach((s) => add(s, false));
  book.abbrevs.forEach((s) => add(s, true));
});

// Between words: a space or a no-break space, never a line break.
const S = '[ \\u00a0]';
// A hyphen, the Unicode hyphens and dashes (U+2010 to U+2014), or a minus sign.
const DASH = '[\\u002d\\u2010-\\u2014\\u2212]';
const DASHES = /[\u2010-\u2014\u2212]/g;
const V = '\\d{1,3}[a-c]?';
const pattern = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/ /g, S);
// Longest first, so "Song of Solomon" is tried before anything shorter.
const alternation = (map) => Array.from(map.keys())
  .sort((a, b) => b.length - a.length)
  .map((spelling) => pattern(spelling) + (map.get(spelling).abbrev ? '\\.?' : ''))
  .join('|');

// A number that starts the next numbered book ("Romans 8:28, 1 Corinthians
// 13:4") is never a chapter, a verse in a list or the end of a range.
const NOT_A_BOOK_NUMBER = `(?!${S}?(?:${alternation(NUMBERED)})(?![A-Za-z]))`;
const CHAPTER = `(\\d{2,3}|[4-9]|[1-3]${NOT_A_BOOK_NUMBER})`;
const VERSES = `:${V}(?:${S}?${DASH}${S}?(?:\\d{1,3}:)?${V}${NOT_A_BOOK_NUMBER})?`
  + `(?:,${S}?${V}(?:${S}?${DASH}${S}?${V})?(?![\\d:])${NOT_A_BOOK_NUMBER})*`;
const SPOKEN = `,?${S}(?:verses?|vv?\\.)${S}?${V}(?:${S}?(?:${DASH}|through|to|and)${S}?${V})?`;
const SPEC = `${S}(?:chapter${S})?${CHAPTER}(?:(${VERSES}|${SPOKEN})|${S}?${DASH}${S}?(\\d{1,3})(?![\\d:])${NOT_A_BOOK_NUMBER})?`;
const TAIL = `(?!\\d|:\\d|[.,]\\d|[A-Za-z]|${S}?(?:a\\.?m\\.?|p\\.?m\\.?)(?![A-Za-z])|%)`;
const PREFIX = `(?:([123])${S}?|(III|II|I)${S}|(First|Second|Third|first|second|third|1st|2nd|3rd)${S})`;
const ORDINALS = { first: 1, second: 2, third: 3, '1st': 1, '2nd': 2, '3rd': 3 };

// Groups: 1-3 the book's number (digit, Roman, word), 4 a numbered book's
// spelling, 5 any other book's, 6 the chapter, 7 the verses, 8 a chapter range's end.
const REF = new RegExp(`(?:${PREFIX}(${alternation(NUMBERED)})|(${alternation(PLAIN)}))${SPEC}${TAIL}`, 'g');
// "; 5:24", ", 5:24", " and 5:24" or ", or 5:24" right where a reference ended
// (sticky: only there). Always a chapter:verse: a bare number after "and" is
// as often a count as a chapter.
const MORE = new RegExp(`(${S}?;${S}?|,${S}?(?:(?:and|or)${S})?|${S}(?:and|or)${S})(\\d{1,3})(${VERSES})${TAIL}`, 'y');
// A word that makes the number before it a count: "Mark 2 people", "John 3 times".
const COUNTED = /^[ \u00a0](?:times?|people|persons?|men|women|kids|children|guys|friends|days?|weeks?|months?|years?|hours?|minutes?|seconds?|things?|ways?|steps?|percent)(?![A-Za-z])/;
const ORDINAL_PSALM = /(\d{1,3})(?:st|nd|rd|th)[ \u00a0]Psalm(?![A-Za-z])/g;
// What may not come right before a reference.
const WORD_BEFORE = /\w/;
const NUMBER_BEFORE = /(?:^|[^\w:.,])(?:\d{1,2}(?:st|nd|rd|th)?|IV|[Ff]ourth|[Ff]ifth)[ \u00a0]$/;
// What may stand between an ambiguous name's reference and the one that confirms it.
const JOIN = /^[ \u00a0,;()]*(?:and|or|&amp;|&)?[ \u00a0,;()]*$/;

/** "3:16, 18" or ", verses 1 through 8" as "16,18" or "1-8": the verses, the way a search reads them. */
function verseList(text) {
  const spoken = !text.startsWith(':');
  let out = spoken
    ? text.replace(/^,?[ \u00a0]*(?:verses?|vv?\.)[ \u00a0]*/, '').replace(/[ \u00a0]*(?:through|to)[ \u00a0]*/, '-').replace(/[ \u00a0]*and[ \u00a0]*/, ',')
    : text.slice(1);
  out = out.replace(DASHES, '-').replace(/[ \u00a0]/g, '').replace(/(\d)[a-c]/g, '$1');
  return out;
}

/**
 * The passage a book, chapter and verse text name, as { query, usfm }, or
 * null when the book has no such chapter or verse. `query` is the plain
 * reference a search takes ("1 Corinthians 13:4-7"); `usfm` is the
 * passage's code ("1CO.13.4-7"), which only YouVersion reads.
 */
function passageOf(book, chapter, verseText, chapterEnd) {
  const okVerse = (v) => v >= 1 && v <= MAX_VERSE;
  if (book.verses) {
    // One chapter: "Jude 3" is verse 3, "Jude 24-25" verses 24 to 25, "Jude 1" the whole book.
    const within = (v) => v >= 1 && v <= book.verses;
    if (verseText) {
      const verses = verseList(verseText);
      if (chapter !== 1 || /:/.test(verses) || !verses.split(/[-,]/).map(Number).every(within)) return null;
      return { query: `${book.name} 1:${verses}`, usfm: `${book.code}.1.${verses}` };
    }
    if (chapterEnd != null) {
      if (!within(chapter) || !(chapterEnd > chapter && within(chapterEnd))) return null;
      return { query: `${book.name} 1:${chapter}-${chapterEnd}`, usfm: `${book.code}.1.${chapter}-${chapterEnd}` };
    }
    if (chapter === 1) return { query: `${book.name} 1`, usfm: `${book.code}.1` };
    return within(chapter) ? { query: `${book.name} 1:${chapter}`, usfm: `${book.code}.1.${chapter}` } : null;
  }
  if (chapter < 1 || chapter > book.chapters) return null;
  if (chapterEnd != null) {
    if (chapterEnd <= chapter || chapterEnd > book.chapters) return null;
    return { query: `${book.name} ${chapter}-${chapterEnd}`, usfm: `${book.code}.${chapter}-${chapterEnd}` };
  }
  const name = book.name === 'Psalms' ? 'Psalm' : book.name;
  if (!verseText) return { query: `${name} ${chapter}`, usfm: `${book.code}.${chapter}` };
  const verses = verseList(verseText);
  // "3:16-4:2" runs into a later chapter the book has.
  const cross = /-(\d{1,3}):/.exec(verses);
  if (cross && (Number(cross[1]) <= chapter || Number(cross[1]) > book.chapters)) return null;
  if (!verses.replace(/-\d{1,3}:/, '-').split(/[-,]/).map(Number).every(okVerse)) return null;
  return { query: `${name} ${chapter}:${verses}`, usfm: `${book.code}.${chapter}.${verses.replace(':', '.')}` };
}

/** One match of REF as a reference, or null when the book, chapter or verses aren't real. */
function readRef(src, m) {
  const numbered = m[4] !== undefined;
  const entry = (numbered ? NUMBERED : PLAIN).get((numbered ? m[4] : m[5]).replace(/\u00a0/g, ' ').replace(/\.$/, ''));
  if (!entry) return null;
  const n = numbered ? (m[1] ? Number(m[1]) : m[2] ? m[2].length : ORDINALS[m[3].toLowerCase()]) : 0;
  const row = numbered ? entry.rows[n] : entry.row;
  if (row === undefined) return null; // 3 Peter
  const book = INFO[row];
  const verseText = m[7] || '';
  if (entry.abbrev && !verseText) return null; // "Rom 8": an abbreviation needs a verse
  const chapterEnd = m[8] === undefined ? null : Number(m[8]);
  const passage = passageOf(book, Number(m[6]), verseText, chapterEnd);
  if (!passage) return null;
  const confirmed = !book.amb || Boolean(verseText) || chapterEnd != null || /chapter/.test(m[0]);
  // "Mark 2 people": a name and a count, whatever reference stands beside it.
  if (!confirmed && COUNTED.test(src.slice(m.index + m[0].length, m.index + m[0].length + 12))) return null;
  return Object.assign({ start: m.index, end: m.index + m[0].length, book, confirmed }, passage);
}

/**
 * Every Bible reference in `text`, in order: [{ start, end, display, query,
 * usfm }]. Works on plain text or on HTML-escaped text: a reference never
 * holds & < > " or '. `display` is the words as written; `query` and `usfm`
 * are what passageOf says.
 */
export function findScripture(text) {
  const src = typeof text === 'string' ? text : '';
  if (!/\d/.test(src)) return [];
  const found = [];
  REF.lastIndex = 0;
  for (let m = REF.exec(src); m; m = REF.exec(src)) {
    // Where a lookbehind would have said no: try again one character on.
    const before = src.slice(Math.max(0, m.index - 12), m.index);
    if (WORD_BEFORE.test(before.slice(-1)) || (m[4] === undefined && NUMBER_BEFORE.test(before))) {
      REF.lastIndex = m.index + 1;
      continue;
    }
    const ref = readRef(src, m);
    if (!ref) continue;
    found.push(ref);
    let at = ref.end;
    for (;;) {
      MORE.lastIndex = at;
      const more = MORE.exec(src);
      const passage = more && passageOf(ref.book, Number(more[2]), more[3], null);
      if (!passage) break;
      found.push(Object.assign({ start: at + more[1].length, end: MORE.lastIndex, confirmed: true }, passage));
      at = MORE.lastIndex;
    }
    REF.lastIndex = at;
  }
  ORDINAL_PSALM.lastIndex = 0;
  for (let o = ORDINAL_PSALM.exec(src); o; o = ORDINAL_PSALM.exec(src)) {
    const chapter = Number(o[1]);
    const start = o.index;
    const end = start + o[0].length;
    if (WORD_BEFORE.test(src.charAt(start - 1)) || chapter < 1 || chapter > 150) continue;
    if (found.some((r) => r.start < end && start < r.end)) continue;
    found.push({ start, end, confirmed: true, query: `Psalm ${chapter}`, usfm: `PSA.${chapter}` });
  }
  found.sort((a, b) => a.start - b.start);
  // An ambiguous name with a bare chapter stands beside a confirmed reference
  // in the same list, on either side: one pass each way reaches the whole run.
  const keep = found.map((r) => r.confirmed);
  const joined = (a, b) => b.start - a.end <= 16 && JOIN.test(src.slice(a.end, b.start));
  for (let k = 1; k < found.length; k++) {
    if (!keep[k] && keep[k - 1] && joined(found[k - 1], found[k])) keep[k] = true;
  }
  for (let k = found.length - 2; k >= 0; k--) {
    if (!keep[k] && keep[k + 1] && joined(found[k], found[k + 1])) keep[k] = true;
  }
  return found
    .filter((_, k) => keep[k])
    .map(({ start, end, query, usfm }) => ({ start, end, display: src.slice(start, end), query, usfm }));
}

// Citation-shaped bracket text left in an answer ("[Title | 12:30]" that
// didn't resolve) is a sermon's name, not a verse to look up.
const CITATION_BRACKETS = /\[[^[\]\n]*\|[^[\]\n]*\]/g;

const attr = (value) => String(value).replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

/**
 * HTML-escaped text with each reference in it marked as a quiet link:
 * <span class="cv-scripture" role="link" tabindex="0" data-ref data-usfm
 * data-no-track>. No URL and no aria-label: the passage's address is built
 * when it is tapped (scriptureUrl), and a screen reader hears the words on
 * screen, like any link. The analytics count the press "Scripture" when a
 * passage opens (ChatView), never which verse, and never a click on the
 * reference itself: a drag or a double-click on one is a click too.
 */
export function linkScripture(html) {
  const refs = findScripture(html);
  if (!refs.length) return html;
  const brackets = [];
  CITATION_BRACKETS.lastIndex = 0;
  for (let b = CITATION_BRACKETS.exec(html); b; b = CITATION_BRACKETS.exec(html)) brackets.push({ from: b.index, to: b.index + b[0].length });
  let out = '';
  let at = 0;
  refs.forEach((r) => {
    if (brackets.some((b) => r.start < b.to && b.from < r.end)) return;
    out += `${html.slice(at, r.start)}<span class="cv-scripture" role="link" tabindex="0" data-ref="${attr(r.query)}" data-usfm="${attr(r.usfm)}" data-no-track>${r.display}</span>`;
    at = r.end;
  });
  return out + html.slice(at);
}

// ---------------------------------------------------------------------------
// Where a tap goes. Three fixed sites and five Bibles, chosen by name in
// meta/publicConfig (functions/limits.js holds the same lists): the doc
// anyone can read never holds anything shaped like a link.
// ---------------------------------------------------------------------------

export const SCRIPTURE_SITES = ['biblegateway', 'google', 'youversion'];
export const SCRIPTURE_VERSIONS = ['NIV', 'NKJV', 'ESV', 'KJV', 'NLT'];
// YouVersion's number for each Bible (bible.com/bible/<id>/…).
const YOUVERSION_IDS = { NIV: 111, NKJV: 114, ESV: 59, KJV: 1, NLT: 116 };

/**
 * The four fields when meta/publicConfig says nothing (each one changes with
 * scripts/set-public-config.js, no deploy):
 *   scriptureLinks    0: references are plain words; 1: a tap opens the
 *                     passage; 2: the same, with a faint dotted line under each
 *   scriptureSite     where it opens: biblegateway, google or youversion
 *   scriptureVersion  the Bible: NIV, NKJV, ESV, KJV or NLT (Google ignores it)
 *   scriptureSpeech   1: Rob's voice says references his way (a later step)
 */
export const SCRIPTURE_DEFAULTS = { scriptureLinks: 0, scriptureSite: 'biblegateway', scriptureVersion: 'NIV', scriptureSpeech: 0 };
const RANGES = { scriptureLinks: [0, 2], scriptureSpeech: [0, 1] };

/** A number (or digits in a string), whole and pulled into range, or null: giveCore.js wholeNumber, and the server's publicNumber. */
function wholeNumber(value, range) {
  let n = null;
  if (typeof value === 'number') n = Number.isFinite(value) ? value : null;
  else if (typeof value === 'string' && value.trim() !== '') n = Number.isFinite(Number(value.trim())) ? Number(value.trim()) : null;
  return n === null ? null : Math.min(range[1], Math.max(range[0], Math.floor(n)));
}

/**
 * The scripture fields in `source` (meta/publicConfig, or an answer's
 * metadata) that are there and pass their rule, the numbers clamped. A field
 * that is missing or bad is left out, so it never moves what the browser has.
 */
export function validScripture(source) {
  const s = source && typeof source === 'object' ? source : {};
  const out = {};
  Object.keys(RANGES).forEach((key) => {
    const n = wholeNumber(s[key], RANGES[key]);
    if (n !== null) out[key] = n;
  });
  if (SCRIPTURE_SITES.includes(s.scriptureSite)) out.scriptureSite = s.scriptureSite;
  if (SCRIPTURE_VERSIONS.includes(s.scriptureVersion)) out.scriptureVersion = s.scriptureVersion;
  return out;
}

/** 0, 1 or 2 (SCRIPTURE_DEFAULTS): the setting, but at least 1 in a browser marked as the team's. */
export function scriptureMode(cfg, internal) {
  const links = wholeNumber(cfg && cfg.scriptureLinks, RANGES.scriptureLinks);
  return Math.max(links === null ? 0 : links, internal ? 1 : 0);
}

const QUERY = /^(?:[1-3] )?[A-Z][A-Za-z ]{1,20} \d{1,3}(?:[-:,]\d{1,3})*$/;
const USFM = /^[1-3A-Z][A-Z]{2}\.\d{1,3}(?:\.\d{1,3}(?:-\d{1,3})?)?$/;

/**
 * The passage's address on the configured site, or null for something that
 * isn't a reference. `ref` is a found reference ({ query, usfm }) or a marked
 * span's dataset ({ ref, usfm }). YouVersion takes a chapter, a verse or a
 * verse range; anything else (a list, a range across chapters) goes to Bible
 * Gateway, which reads them all.
 *   biblegateway  https://www.biblegateway.com/passage/?search=<query>&version=<version>
 *   google        https://www.google.com/search?q=<query>
 *   youversion    https://www.bible.com/bible/<id>/<usfm>.<version>
 */
export function scriptureUrl(cfg, ref) {
  const chosen = Object.assign({}, SCRIPTURE_DEFAULTS, validScripture(cfg));
  const site = chosen.scriptureSite;
  const version = chosen.scriptureVersion;
  const query = String((ref && (ref.query || ref.ref)) || '');
  if (!QUERY.test(query)) return null;
  const usfm = String((ref && ref.usfm) || '');
  if (site === 'youversion' && USFM.test(usfm)) {
    return `https://www.bible.com/bible/${YOUVERSION_IDS[version]}/${encodeURIComponent(usfm)}.${encodeURIComponent(version)}`;
  }
  if (site === 'google') return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
  return `https://www.biblegateway.com/passage/?search=${encodeURIComponent(query)}&version=${encodeURIComponent(version)}`;
}

// ---------------------------------------------------------------------------
// A tap, or a selection? Pressing and holding, or dragging, selects a
// reference's words like any others ("Ask about this" depends on it), and
// must never open the passage.
// ---------------------------------------------------------------------------

const DRAG_PX = 4;

/**
 * True when a click on a reference ended a selection gesture, so it must not
 * open. Pure; ChatView supplies the facts:
 *   down              { x, y, pointerType, hadSelection } from the pointerdown
 *                     on this reference (null when there was none: a click a
 *                     screen reader made, say). hadSelection: words were
 *                     selected in the conversation as it went down
 *   click             { x, y, detail } from the click; detail 0 is Enter
 *   selectionTouches  words are selected in or across the reference now
 * Enter always opens. Any pointer: the second click of a double-click, or a
 * selection touching the reference. A mouse also: a drag over 4 px. Touch
 * and pen also: a tap that only cleared a selection; never the distance or
 * the time. A finger moves a few pixels in any tap, and a press that travels
 * is a scroll; a press the phone takes as a long-press selects the words; the
 * browser ends both with no click at all. A click that does come is a tap,
 * however slow: Android's "Touch & hold delay" gives someone up to 1.5 s for
 * one. A mouse click opens even with words selected elsewhere, in the message
 * box say.
 */
export function isSelectionGesture({ down = null, click = {}, selectionTouches = false } = {}) {
  const detail = Number(click.detail) || 0;
  if (detail === 0) return false;
  if (detail > 1 || selectionTouches) return true;
  if (!down) return false;
  if (down.pointerType === 'mouse') return Math.hypot((click.x || 0) - (down.x || 0), (click.y || 0) - (down.y || 0)) > DRAG_PX;
  return Boolean(down.hadSelection);
}
